import React, { useCallback, useEffect, useMemo, useState } from "react";
import '.././popup.common.css'
import CustomModal from "../../Modal/CustomModal";
import RefundStep from "./RefundStep/RefundStep";
import ReasonStep from "./ReasonStep/ReasonStep";
import { getPaymentDetails, getSalesDetails } from "../../../APICalling/APICalling";
import { useGlobalContext } from "../../../Context/GlobalContext";
import CommissionHandlingStep from "./CommissionHandlingStep/CommissionHandlingStep";
import CreditGiftReturnStep from "./CreditGiftReturnStep/CreditGiftReturnStep";

const VoidSalePopup = ({onClose,voidedSaleWithRefund,bookingDetails: bookingDataFromAPIProp,bookingDataFromAPI,from = "home",
}) => {
	const { bookingDetails: bookingDataFromAPIFromContext } = useGlobalContext();
	const bookingDetails =from === "salePage"? (bookingDataFromAPIProp || bookingDataFromAPI): bookingDataFromAPIFromContext;
	const bookingId = bookingDetails?.id || bookingDetails?.booking_id;
	const saleId = bookingDetails?.sale_id || bookingDetails?.sales_id || (from === "salePage" ? bookingDetails?.id : null);
	const bookingType = String(bookingDetails?.booking_type || bookingDetails?.type || "").toLowerCase();
	const shouldShowCommissionStep = bookingType === "booking";
	const isMembershipSale = bookingType === "membership";
	const isProductSale = bookingDetails?.booking_type === "product" || bookingDetails?.type === "product";
	const paymentQueryParams = useMemo(() => {
		if (!isProductSale || !bookingId) return undefined;
		return { sale_id: bookingId };
	}, [isProductSale, bookingId]);
	const [firstStep, setFirstStep] = useState(true);
	const [secondStep, setSecondStep] = useState(false);
	const [thirdStep, setThirdStep] = useState(false);
	const [paymentDetails, setPaymentDetails] = useState(null);
	const [membershipSaleDetail, setMembershipSaleDetail] = useState(null);
	const [loading, setLoading] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [submitErrorMessage, setSubmitErrorMessage] = useState("");
	const [commissionPayload, setCommissionPayload] = useState({
		clawback_commission: false,
		booking_item_ids: [],
		remark: ""
	});
	const [membershipRefundPayload, setMembershipRefundPayload] = useState({
		refund_amount: 0,
		max_refundable_amount: 0,
		gifts: [],
	});
	
	const fetchPaymentDetails = useCallback(async () => {
		try {
			setLoading(true);
			if (!bookingId) {
				setPaymentDetails([]);
				setMembershipSaleDetail(null);
				return;
			}
			const [response, membershipDetailResponse] = await Promise.all([
				getPaymentDetails(saleId, paymentQueryParams),
				isMembershipSale && saleId
					? getSalesDetails(saleId, { panel: "outlet", type: "membership" })
					: Promise.resolve(null),
			]);
			const data = response?.data?.data;
			// console.log("Payment Details:", data);
			setPaymentDetails(data || []);
			const membershipDetail = membershipDetailResponse?.data?.data ?? membershipDetailResponse?.data ?? null;
			setMembershipSaleDetail(membershipDetail?.id ? membershipDetail : null);
		} catch (error) {
			console.error("Error:", error);
			setPaymentDetails([]);
			setMembershipSaleDetail(null);
		} finally {
			setLoading(false);
		}
	}, [bookingId, isMembershipSale, paymentQueryParams, saleId]);

	useEffect(() => {
		fetchPaymentDetails();
	}, [fetchPaymentDetails]);

	const goToCommission = () => {
		setSubmitErrorMessage("");
		setFirstStep(false);
		setSecondStep(shouldShowCommissionStep || isMembershipSale);
		setThirdStep(!shouldShowCommissionStep && !isMembershipSale);
	}
	const goToReason = () => {
		setSubmitErrorMessage("");
		setFirstStep(false);
		setSecondStep(false);
		setThirdStep(true);
	}

	
	const backStep = () => {
		setSubmitErrorMessage("");
		setFirstStep(!shouldShowCommissionStep && !isMembershipSale);
		setThirdStep(false);
		setSecondStep(shouldShowCommissionStep || isMembershipSale);
	}

	const backToRefund = () => {
		setSubmitErrorMessage("");
		setFirstStep(true);
		setSecondStep(false);
		setThirdStep(false);
	}

	const handleCommissionChange = useCallback((payload) => {
		setCommissionPayload(payload);
	}, []);

	const handleMembershipRefundChange = useCallback((payload) => {
		setMembershipRefundPayload(payload);
	}, []);

	const handleSendRefundReq = async () => {
		try {
			setProcessing(true);
			setSubmitErrorMessage("");
			const result = await voidedSaleWithRefund(
				shouldShowCommissionStep
					? commissionPayload
					: {
						clawback_commission: false,
						booking_item_ids: [],
						remark: "",
						...(isMembershipSale ? { membership_refund: membershipRefundPayload } : {}),
					}
			);
			if (result && result.success === false) {
				setSubmitErrorMessage(result.message || "Failed to process refund. Please try again.");
			}
		} catch (error) {
			console.error("Error in handleSendRefundReq:", error);
			setSubmitErrorMessage(
				error?.response?.data?.message ||
				error?.message ||
				"Failed to process refund. Please try again."
			);
		} finally {
			setProcessing(false);
		}
	}

	return (
		<CustomModal
			modalClass={`cancelBookingPopup`}
			show={true}
			onHide={onClose}
		>
		{firstStep && (
			<RefundStep
				paymentDetails={paymentDetails}
				nextStep={goToCommission}
				voidSale={onClose}
				heading="Process Refund"
				skipButtonText="Cancel"
				loading={loading}
			/>
		)}
		{secondStep && shouldShowCommissionStep && (
			<CommissionHandlingStep
				saleId={saleId}
				backStep={backToRefund}
				nextStep={goToReason}
				onCommissionChange={handleCommissionChange}
			/>
		)}
		{secondStep && isMembershipSale && (
			<CreditGiftReturnStep
				detail={membershipSaleDetail || bookingDetails}
				backStep={backToRefund}
				nextStep={goToReason}
				onChange={handleMembershipRefundChange}
			/>
		)}
		{thirdStep && (
			<ReasonStep
				onClose={onClose}
				backStep={backStep}
				sendRefundReq={handleSendRefundReq}
				heading="Reason for Refund"
				para="Please provide a reason for refunding this sale"
				action="voidSale"
				processing={processing}
				externalError={submitErrorMessage}
			/>
		)}

		</CustomModal>
	);
};

export default VoidSalePopup;
